import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { liff } from '@line/liff';
import RootLayout from 'layouts/RootLayout';
import { registerSource } from '../apis/sourceService';

const LIFF_ID = process.env.REACT_APP_SOURCE_LIFF_ID;
const SESSION_KEY = 'source_keyword';

/**
 * 取得 keyword 的優先順序：
 * 1. URL path param (/source/:keyword)
 * 2. liff.state query param (/source?liff.state=/keyword)
 * 3. sessionStorage (liff.init redirect 後恢復)
 */
function resolveKeyword(
  paramKeyword: string | undefined,
  searchParams: URLSearchParams,
): string | null {
  if (paramKeyword) return paramKeyword;

  const liffState = searchParams.get('liff.state');
  if (liffState) {
    const kw = liffState.replace(/^\//, '');
    if (kw) {
      sessionStorage.setItem(SESSION_KEY, kw);
      return kw;
    }
  }

  const saved = sessionStorage.getItem(SESSION_KEY);
  if (saved) return saved;

  return null;
}

const SourceWelcome = () => {
  const { keyword: paramKeyword } = useParams<{ keyword: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [sourceName, setSourceName] = useState('');
  const [action, setAction] = useState<'new' | 'overwrite' | 'unchanged'>('new');
  const [previousName, setPreviousName] = useState<string | null>(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const init = async () => {
      const keyword = resolveKeyword(paramKeyword, searchParams);

      if (!keyword) {
        setError('連結無效');
        setLoading(false);
        return;
      }

      try {
        await liff.init({ liffId: LIFF_ID! });

        if (!liff.isLoggedIn()) {
          liff.login();
          return;
        }

        const accessToken = liff.getAccessToken();
        const profile = await liff.getProfile();

        if (!accessToken || !profile?.userId) {
          setError('無法取得 LINE 資料');
          setLoading(false);
          return;
        }

        const result = await registerSource(
          accessToken,
          profile.userId,
          keyword,
          profile.displayName,
          profile.pictureUrl,
        );

        // 成功後清除 sessionStorage
        sessionStorage.removeItem(SESSION_KEY);

        setSourceName(result.source.name);
        setAction(result.action);
        setPreviousName(result.previousSource?.name || null);
        setHasProfile(result.hasProfile);
        setLoading(false);
      } catch (err: unknown) {
        console.error('來源註冊失敗：', err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('連線資料有誤');
        }
        setLoading(false);
      }
    };

    init();
  }, [paramKeyword, searchParams]);

  if (loading) {
    return (
      <RootLayout title="歡迎加入">
        <div className="flex justify-center items-center h-screen">
          <p>載入中...</p>
        </div>
      </RootLayout>
    );
  }

  if (error) {
    return (
      <RootLayout title="歡迎加入">
        <div className="p-6 flex flex-col justify-center items-center">
          <p className="text-red-500 text-lg">{error}</p>
        </div>
      </RootLayout>
    );
  }

  return (
    <RootLayout title="歡迎加入">
      <div className="p-6 flex flex-col justify-center items-center">
        <div className="text-center">
          <p className="text-xl mb-4">
            {action === 'overwrite' ? '關聯機構已更新！' : '歡迎加入！'}
          </p>
          <p className="text-lg">
            {action === 'overwrite' ? (
              <>已更新關聯機構為 <span className="font-bold text-[#9F0050]">{sourceName}</span>
              {previousName && <span className="text-sm text-gray-500">（原：{previousName}）</span>}</>
            ) : (
              <>您是透過 <span className="font-bold text-[#9F0050]">{sourceName}</span> 加入的</>
            )}
          </p>
          <div className="flex flex-col gap-3 mt-6 w-full max-w-xs mx-auto">
            {!hasProfile && (
              <button
                onClick={() => { window.location.href = '/profile'; }}
                className="w-full px-8 py-3 bg-[#9F0050] text-white text-lg rounded-lg hover:bg-[#7a003d] transition"
              >
                前往填寫基本資料
              </button>
            )}
            <button
              onClick={() => {
                if (liff.isInClient()) {
                  liff.closeWindow();
                } else {
                  window.close();
                }
              }}
              className="w-full px-8 py-3 bg-gray-200 text-gray-700 text-lg rounded-lg hover:bg-gray-300 transition"
            >
              關閉視窗
            </button>
          </div>
        </div>
      </div>
    </RootLayout>
  );
};

export default SourceWelcome;
